<template>
  <div
    :class="[
      'rounded-md px-3 py-2.5 flex flex-col gap-1.5 border',
      toneStyles.bg,
      toneStyles.border,
    ]"
  >
    <div class="flex items-center gap-1.5">
      <span
        :class="[
          'w-1.5 h-1.5 rounded-full flex-shrink-0',
          toneStyles.dot,
          !isActive && 'opacity-50',
        ]"
      />
      <span
        :class="[
          'text-[11px] uppercase tracking-wider font-semibold truncate',
          toneStyles.label,
        ]"
      >
        {{ label }}
      </span>
    </div>
    <div
      :class="[
        'text-2xl font-semibold leading-none',
        isActive
          ? toneStyles.text
          : 'text-surface-600 dark:text-surface-400',
      ]"
    >
      {{ value }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

type Tone = 'red' | 'orange' | 'amber' | 'green' | 'blue' | 'neutral'

interface Props {
  label: string
  value: number | string
  tone: Tone
  /**
   * Force-treat as active. Defaults to numeric `value > 0`.
   */
  active?: boolean
}

const props = defineProps<Props>()

interface ToneStyle {
  dot: string
  text: string
  label: string
  bg: string
  border: string
}

// Severity tones (red/orange/green) align with getAlertSeverityColor in FCCUtil.
// State tones (amber/blue) intentionally distinct to avoid collision with Medium.
const TONES: Record<Tone, ToneStyle> = {
  red: {
    dot: 'bg-red-500',
    text: 'text-red-800 dark:text-red-200',
    label: 'text-red-800 dark:text-red-200',
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-500/30 dark:border-red-500/30',
  },
  orange: {
    dot: 'bg-orange-500',
    text: 'text-orange-800 dark:text-orange-200',
    label: 'text-orange-800 dark:text-orange-200',
    bg: 'bg-orange-50 dark:bg-orange-950/30',
    border: 'border-orange-500/30 dark:border-orange-500/30',
  },
  amber: {
    dot: 'bg-amber-500',
    text: 'text-amber-800 dark:text-amber-200',
    label: 'text-amber-800 dark:text-amber-200',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-500/30 dark:border-amber-500/30',
  },
  green: {
    dot: 'bg-green-500',
    text: 'text-green-800 dark:text-green-200',
    label: 'text-green-800 dark:text-green-200',
    bg: 'bg-green-50 dark:bg-green-950/30',
    border: 'border-green-500/30 dark:border-green-500/30',
  },
  blue: {
    dot: 'bg-blue-500',
    text: 'text-blue-800 dark:text-blue-200',
    label: 'text-blue-800 dark:text-blue-200',
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-500/30 dark:border-blue-500/30',
  },
  neutral: {
    dot: 'bg-surface-500',
    text: 'text-surface-800 dark:text-surface-100',
    label: 'text-surface-700 dark:text-surface-200',
    bg: 'bg-surface-100 dark:bg-surface-800',
    border: 'border-surface-300/40 dark:border-surface-600/40',
  },
}

const toneStyles = computed<ToneStyle>(() => TONES[props.tone])

const isActive = computed(() => {
  if (typeof props.active === 'boolean') return props.active
  if (typeof props.value === 'number') return props.value > 0
  return false
})
</script>
